import { useEffect, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { InputPlain } from '@peacecover/component';
import { Chat, LikeLine } from '@peacecover/icon';
import { useIOSKeyboardSheetPadding } from '../../../hooks/useIOSKeyboardSheetPadding';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import { formatDateTime } from '../../../utils/DateUtil';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';

const MOCK_COMMENTS = [
  {
    id: 1,
    content: 'Hello, world!',
    nickname: 'John Doe',
    thumbnailUrl: '',
    createdAt: '2021-01-01',
    commentCounts: 10,
    likeCounts: 10,
    isMutualFollow: true
  },
  {
    id: 2,
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    nickname: 'Anonymous',
    thumbnailUrl: '',
    createdAt: '2021-01-01',
    commentCounts: 0,
    likeCounts: 3,
    isMutualFollow: false
  },
  {
    id: 3,
    content: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    nickname: 'Jane Doe',
    thumbnailUrl: '',
    createdAt: '2021-01-01',
    commentCounts: 0,
    likeCounts: 5,
    isMutualFollow: false
  }
];

interface CommentBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onKeyboardVisibleChange?: (visible: boolean) => void;
}

const CommentBottomSheet = ({
  isOpen,
  onClose,
  onKeyboardVisibleChange
}: CommentBottomSheetProps) => {
  const [comment, setComment] = useState('');
  const { containerRef } = useIOSKeyboardSheetPadding({ enabled: isOpen });
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  useEffect(() => {
    onKeyboardVisibleChange?.(isKeyboardVisible);
  }, [isKeyboardVisible, onKeyboardVisibleChange]);

  useEffect(() => {
    if (!isOpen) {
      onKeyboardVisibleChange?.(false);
    }
  }, [isOpen, onKeyboardVisibleChange]);

  const sheetBottomClass = isKeyboardVisible
    ? '!bottom-0'
    : '!bottom-[calc(70px+env(safe-area-inset-bottom))]';

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content" className={sheetBottomClass}>
      <Sheet.Container className="!rounded-t-[20px] pt-[32px] dark:!bg-background-elevated">
        <Sheet.Content ref={containerRef} className="overflow-x-hidden ">
          {MOCK_COMMENTS.map((comment) => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
          <div className="px-[22px] pt-[16px] pb-[32px] flex items-center gap-[10px] border-t border-black-opacity-50">
            <ProfileAvatar widthAndHeight="34px" />
            <div className="py-[8px] w-full flex items-center justify-between gap-[10px]">
              <InputPlain
                size="small"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="여기에 감상평을 작성해보세요."
                className="bg-transparent font-semibold"
              />
              {comment.trim() && (
                <button
                  type="button"
                  className="flex-shrink-0 text-[15px] font-semibold leading-[18px] text-primary-light dark:text-primary-dark"
                >
                  전송
                </button>
              )}
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-transparent" />
    </Sheet>
  );
};

export default CommentBottomSheet;

const CommentItem = ({ comment }: { comment: (typeof MOCK_COMMENTS)[0] }) => {
  return (
    <div className="px-[22px] py-[16px] flex items-start gap-[10px]">
      <ProfileAvatar
        thumbnailUrl={comment.thumbnailUrl}
        widthAndHeight="34px"
        svgWidthAndHeight="24px"
        className="flex-shrink-0"
      />
      <div className="flex flex-col items-start">
        <div className="flex items-end justify-between w-full pt-[1px] pb-[5px]">
          <div className="flex items-end gap-[6px]">
            <p className="text-[14px] font-semibold leading-[17px] text-text-main-lv2">
              {comment.nickname}
            </p>
            <p className="text-[12px] leading-[14px] text-text-main-lv4">
              {formatDateTime(comment.createdAt, 'hh:mm')}
            </p>
          </div>
          {!comment.isMutualFollow && (
            <p className="text-[12px] leading-[14px] font-medium text-primary-light dark:text-primary-dark">
              팔로우+
            </p>
          )}
        </div>
        <p className="text-[15px] leading-[19px] text-text-main-lv2 pb-[3px]">{comment.content}</p>
        <div className="py-[5px] flex items-center gap-[24px]">
          <div className="flex items-center gap-[4px]">
            <Chat size={18} className="text-text-sub-lv1" />
            <p className="text-[15px] font-medium leading-[18px] text-text-sub-lv1">
              {comment.commentCounts}
            </p>
          </div>
          <div className="flex items-center gap-[4px]">
            <LikeLine size={18} className="text-text-sub-lv1" />
            <p className="text-[15px] font-medium leading-[18px] text-text-sub-lv1">
              {comment.likeCounts}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
